import { inject, provideEnvironmentInitializer } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, type Route, Router } from '@angular/router';

import { map, NEVER } from 'rxjs';

import { AskHRService } from '@core/ask-hr-logic';
import { TellentUserRequestsViewComponent } from '@core/ask-hr-smart-ui';
import {
  AskTemplateNotificationsService,
  CreateTicketDialogComponent,
  CreateTicketDialogService,
  TellentAskTemplatesViewComponent,
  TellentUserTemplatesViewComponent,
} from '@core/ask-hr-templates-smart-ui';
import {
  TellentAskTicketsViewComponent,
  TellentUserTicketsViewComponent,
  TicketDialogComponent,
  TicketDialogService,
  type TicketEmptyStateData,
} from '@core/ask-hr-tickets-smart-ui';
import { type DialogConfigOptions, DialogWidth } from '@core/user-interface-legacy';

export const ROUTES = [
  {
    path: 'ask-hr',
    children: [
      {
        path: 'tickets',
        component: TellentAskTicketsViewComponent,
      },
      {
        path: 'forms',
        component: TellentAskTemplatesViewComponent,
        canActivate: [
          () =>
            inject(AskHRService).canAccessFormsPage() ||
            new RedirectCommand(inject(Router).parseUrl('/ask-hr/my/tickets')),
        ],
      },
      {
        path: 'my',
        component: TellentUserRequestsViewComponent,
        children: [
          { path: 'forms', component: TellentUserTemplatesViewComponent },
          { path: 'tickets', component: TellentUserTicketsViewComponent },
          { path: '**', redirectTo: 'tickets' },
        ],
      },
      { path: '**', redirectTo: 'my/tickets' },
    ],
    providers: [provideEnvironmentInitializer(() => inject(AskTemplateNotificationsService))],
  },
  {
    path: 'ask-hr/create-ticket/:templateId',
    component: CreateTicketDialogComponent,
    data: {
      options: {
        width: DialogWidth.LargeX,
      } satisfies DialogConfigOptions,
    },
    resolve: {
      dialogData: (route: ActivatedRouteSnapshot) =>
        inject(CreateTicketDialogService).resolveDialogData(route),
    },
    canDeactivate: [() => inject(CreateTicketDialogService).canDeactivateGuard()],
    outlet: 'dialog',
    providers: [provideEnvironmentInitializer(() => inject(AskTemplateNotificationsService))],
  },
  {
    path: 'ask-hr/ticket/:ticketId',
    component: TicketDialogComponent,
    resolve: {
      options: () => inject(TicketDialogService).getTicketDialogOptions(),
      dialogData: (route: ActivatedRouteSnapshot) => {
        const ticketDialogService = inject(TicketDialogService);
        return ticketDialogService
          .resolveDialogData(+route.paramMap.get('ticketId')!)
          .pipe(map(data => data ?? ticketDialogService.getNotFoundRedirectCommand()));
      },
    },
    outlet: 'dialog',
  },
  {
    path: 'ask-hr/ticket-not-found',
    resolve: {
      options: () => inject(TicketDialogService).getTicketDialogOptions(),
      dialogData: (): TicketEmptyStateData => inject(TicketDialogService).getTicketEmptyStateData(),
    },
    loadComponent: () =>
      import('@core/ask-hr-tickets-smart-ui').then(m => m.TicketEmptyStateComponent),
    outlet: 'dialog',
  },
  {
    path: '**',
    canActivate: [() => NEVER],
    loadComponent: () => NEVER,
  },
] satisfies Route[];
